"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Car, CarFront, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Packages() {
  const prefersReducedMotion = useReducedMotion();
  const [selectedVehicles, setSelectedVehicles] = useState<Record<string, string>>({
    essential: "",
    full: "",
    interior: ""
  });
  const tiers = [{
    key: "essential",
    eyebrow: "PREMIUM",
    name: "Essential Wash",
    price: "Starting at $90",
    priceSuffix: "",
    vehiclePricing: {
      Sedan: "$90",
      SUV: "$100",
      "Truck/3-row SUV": "$120"
    },
    emphasized: false,
    buttonUrl: "https://wa.me/17867784394",
    benefits: ["Exterior hand wash", "Wheel and tire cleaning", "Window cleaning", "Interior vacuum", "Door jambs wiped clean", "Tire dressing", "Paint Protection Wax included as needed — up to 2–3 months of protection"],
    cta: {
      label: "Book Now",
      variant: "primary" as const
    }
  }, {
    key: "full",
    eyebrow: "LUXURY",
    name: "Full Detail",
    price: "Starting at $270",
    priceSuffix: "",
    vehiclePricing: {
      Sedan: "$270",
      SUV: "$300",
      "Truck/3-row SUV": "$320"
    },
    emphasized: true,
    buttonUrl: "https://wa.me/17867784394",
    benefits: ["Complete exterior hand wash", "Wheel and tire cleaning", "Chemical paint decontamination", "Exterior and interior glass cleaning", "4–6 months of paint sealant protection", "Thorough interior vacuum", "Deep cleaning of seats and carpets", "Stain treatment", "Steam cleaning of detailed interior areas", "Dashboard, console, vents, cup holders, and door panels cleaned"],
    cta: {
      label: "Book Now",
      variant: "primary" as const
    }
  }, {
    key: "paint-correction",
    eyebrow: "ELITE",
    name: "Paint Correction",
    price: "Starting at $500",
    priceSuffix: "",
    emphasized: false,
    buttonUrl: "https://wa.me/17867784394",
    benefits: ["Exterior wash", "Chemical paint decontamination", "Paint inspection", "Machine polishing to reduce swirls, oxidation, light-to-moderate defects", "Panel preparation", "Premium paint sealant (4-6 months of protection)"],
    cta: {
      label: "Book Now",
      variant: "primary" as const
    }
  }, {
    key: "ceramic",
    eyebrow: "ELITE",
    name: "Ceramic Coating",
    price: "Starting at $900",
    priceSuffix: "",
    emphasized: false,
    buttonUrl: "https://wa.me/17867784394",
    benefits: ["Exterior wash and paint decontamination", "One-step paint correction", "Professional ceramic coating application", "Hydrophobic protection", "UV and chemical resistance", "Enhanced gloss and color depth"],
    cta: {
      label: "GET A QUOTE",
      variant: "ghost" as const
    }
  }, {
    key: "interior",
    eyebrow: "STANDARD",
    name: "Interior Deep Cleaning",
    price: "Starting at $150",
    priceSuffix: "",
    emphasized: false,
    buttonUrl: "https://wa.me/17867784394",
    benefits: ["Full interior vacuum", "Seats and carpets cleaned", "Floor mats cleaned", "Dashboard and console cleaned", "Door panels cleaned", "Leather cleaning and conditioning when applicable", "Interior windows cleaned", "Odor treatment"],
    cta: {
      label: "Book Now",
      variant: "primary" as const
    }
  }];
  const cardInitial = prefersReducedMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 32
  };
  const cardAnimate = {
    opacity: 1,
    y: 0
  };
  return <section data-section-id="28760" id="detailing-packages-paquetes-de-detallado" data-nav="dark" className="bg-background text-foreground relative overflow-hidden" style={{
    paddingTop: "clamp(120px, 14vw, 200px)",
    paddingBottom: "clamp(120px, 14vw, 200px)"
  }}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-1/3 mx-auto h-[600px] w-[600px] max-w-full opacity-40 blur-3xl" style={{
      background: "radial-gradient(circle at center, hsl(41 62% 62% / 0.14) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto w-full" style={{
      maxWidth: "1440px",
      paddingLeft: "clamp(20px, 5vw, 88px)",
      paddingRight: "clamp(20px, 5vw, 88px)"
    }}>
        <div className="mx-auto max-w-[720px] text-center" style={{
        marginBottom: "clamp(64px, 8vw, 96px)"
      }}>
          <motion.p initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.6
        }} transition={{
          duration: 0.6,
          ease: [0.19, 1, 0.22, 1]
        }} className="font-sans text-[0.78rem] font-medium uppercase" style={{
          letterSpacing: "0.28em",
          color: "var(--muted-foreground)"
        }}>
            SERVICES
          </motion.p>

          <motion.h2 initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.5
        }} transition={{
          duration: 0.8,
          ease: [0.19, 1, 0.22, 1],
          delay: 0.08
        }} className="font-serif font-normal text-foreground mt-6" style={{
          fontSize: "clamp(2.25rem, 4.6vw, 4.5rem)",
          lineHeight: 1.05,
          letterSpacing: "-0.015em"
        }}>
            Precision <em className="italic">detailing</em> for your vehicle.
          </motion.h2>

          <motion.p initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.5
        }} transition={{
          duration: 0.8,
          ease: [0.19, 1, 0.22, 1],
          delay: 0.16
        }} className="font-sans mt-8 mx-auto" style={{
          fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
          lineHeight: 1.7,
          color: "var(--muted-foreground)",
          maxWidth: "560px"
        }}>Premium mobile detailing tailored to your vehicle’s needs.</motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-6">
          {tiers.map((tier, i) => <motion.div data-index={i} key={tier.key} initial={cardInitial} whileInView={cardAnimate} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.85,
          ease: [0.22, 1, 0.36, 1],
          delay: prefersReducedMotion ? 0 : i * 0.12
        }} className="group relative flex flex-col transition-colors duration-300" style={{
          backgroundColor: "hsl(258 16% 9% / 0.6)",
          border: tier.emphasized ? "1px solid hsl(41 62% 62% / 0.3)" : "1px solid hsl(258 14% 14%)",
          padding: "clamp(2rem, 3vw, 2.75rem)",
          minHeight: "620px"
        }}>
              <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-32 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-b-[20px]" style={{
            background: "linear-gradient(to top, hsl(41 62% 62% / 0.08), transparent)"
          }} />

              <p className="font-sans font-medium uppercase" style={{
            fontSize: "0.78rem",
            letterSpacing: "0.28em",
            color: "var(--ivory-muted)"
          }}>
                {tier.eyebrow}
              </p>

              <h3 className="font-serif font-normal text-[var(--gold)] mt-6" style={{
            fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)",
            lineHeight: 1.15,
            letterSpacing: "-0.005em"
          }}>
                {tier.name}
              </h3>

              {tier.key === "essential" && <div className="mt-2 flex flex-col gap-1">
                  <p className="font-sans text-xs font-medium text-[var(--gold)] leading-snug">
                    Best for regularly maintained vehicles that are washed frequently.
                  </p>
                  <p className="font-sans text-[0.7rem] text-[var(--ivory-muted)] leading-relaxed">
                    Vehicles with heavy dirt, stains, excessive buildup, or neglected interiors may require an Interior Deep Cleaning or Full Detail.
                  </p>
                </div>}

              {(tier.key === "full" || tier.key === "ceramic") && <div className="mt-2.5">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-[0.7rem] font-medium tracking-widest uppercase bg-black text-[var(--gold)] border border-[hsl(41_62%_62%/0.4)]">
                    Most Popular
                  </span>
                </div>}

              {tier.key !== "essential" && tier.key !== "full" && <div className="font-sans font-normal mt-2 text-sm flex items-baseline gap-1.5">
                  <span style={{
              color: "var(--muted-foreground)"
            }}>Starting at</span>
                  <span className="font-semibold" style={{
              color: "var(--gold)"
            }}>{tier.price.replace(/^Starting at\s*/i, "")}</span>
                </div>}

              {tier.vehiclePricing && (tier.key === "essential" || tier.key === "full" ? <div className="mt-3 flex flex-col gap-2">
                    <div className="grid grid-cols-3 gap-1.5">
                      {Object.entries(tier.vehiclePricing).map(([vehicle, cost], vIdx) => {
                const isSelected = selectedVehicles[tier.key] === vehicle;
                const VehicleIcon = vehicle === "Sedan" ? Car : vehicle === "SUV" ? CarFront : Truck;
                return <Button type="button" key={vehicle} data-index={vIdx} onClick={() => setSelectedVehicles(prev => ({
                  ...prev,
                  [tier.key]: vehicle
                }))} variant="ghost" className={`h-auto px-1.5 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer flex flex-col items-center justify-center text-center leading-tight whitespace-normal ${isSelected ? "bg-[hsl(41_62%_62%/0.15)] text-[var(--gold)] border border-[var(--gold)] hover:bg-[hsl(41_62%_62%/0.2)] hover:text-[var(--gold)]" : "bg-black/30 text-[var(--ivory-muted)] border border-[hsl(258_14%_14%)] hover:border-[hsl(38_14%_68%/0.5)] hover:text-[var(--ivory)] hover:bg-black/40"}`}>
                            <VehicleIcon className={`w-4 h-4 ${isSelected ? "text-[var(--gold)]" : "text-[var(--ivory-muted)]"}`} />
                            <span className="text-[0.7rem] sm:text-xs font-medium leading-tight mt-1">{vehicle}</span>
                          </Button>;
              })}
                    </div>
                    {selectedVehicles[tier.key] ? <p className="text-xs text-center mt-1 text-[var(--ivory-muted)]">
                        Selected: <span className="font-medium text-foreground">{selectedVehicles[tier.key]}</span> &mdash; <span className="font-semibold text-[var(--gold)]">{tier.vehiclePricing[selectedVehicles[tier.key]]}</span>
                      </p> : <p className="text-xs text-center mt-1 text-[var(--ivory-muted)]">
                        Select a vehicle type to see pricing
                      </p>}
                  </div> : <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-sm">
                    {Object.entries(tier.vehiclePricing).map(([vehicle, cost], vIdx) => <div key={vIdx} data-index={vIdx} className="flex flex-col text-sm">
                        <span className="font-medium" style={{
                color: "var(--gold)"
              }}>{vehicle}</span>
                        <span style={{
                color: "var(--gold)"
              }}>{cost}</span>
                      </div>)}
                  </div>)}

              <div className="mt-8 h-px w-full" style={{
            backgroundColor: "hsl(258 14% 14%)"
          }} aria-hidden="true" />

              <ul className="mt-8 flex flex-col gap-4 relative z-10">
                {tier.benefits.map((benefit, j) => <li data-index={j} key={j} className="flex items-start gap-3">
                    <span className="flex-shrink-0 mt-1 flex items-center justify-center" style={{
                width: "16px",
                height: "16px"
              }}>
                      <Check style={{
                  width: "16px",
                  height: "16px",
                  color: "var(--gold)"
                }} strokeWidth={1.5} />
                    </span>
                    <span className="font-sans" style={{
                fontSize: "0.95rem",
                lineHeight: 1.7,
                color: "var(--ivory-muted)"
              }}>
                      {benefit}
                    </span>
                  </li>)}
              </ul>

              <div className="mt-auto pt-10 relative z-10">
                {tier.cta.variant === "primary" ? <Button asChild className="w-full rounded-lg font-sans font-medium uppercase h-auto" style={{
              backgroundColor: "transparent",
              border: "1px solid hsl(41 62% 62% / 0.55)",
              color: "var(--ivory)",
              padding: "0.95rem 1.75rem",
              fontSize: "0.875rem"
            }}>
                    <Link to={tier.buttonUrl}>{tier.cta.label}</Link>
                  </Button> : <Button asChild variant="outline" className="w-full rounded-lg font-sans font-medium uppercase h-auto bg-transparent" style={{
              border: "1px solid hsl(38 14% 68% / 0.35)",
              color: "var(--ivory)",
              padding: "0.95rem 1.75rem",
              fontSize: "0.875rem"
            }}>
                    <Link to={tier.buttonUrl}>{tier.cta.label}</Link>
                  </Button>}
              </div>
            </motion.div>)}
        </div>

        <div className="mt-24 mx-auto max-w-[720px] bg-[hsl(258_16%_9%_/_0.6)] border border-[hsl(258_14%_14%)] rounded-lg p-10">
          <h3 className="font-serif text-3xl text-foreground mb-8 text-center">Add-Ons</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["Pet Hair Removal", "Engine Bay Cleaning", "Headlight Restoration", "Plastic Trim Restoration", "Seat Shampoo"].map((item, i) => <li key={i} data-index={i} className="flex justify-between items-center border-b border-[hsl(258_14%_14%)] py-3">
                <span className="font-sans text-ivory-muted">{item}</span>
                <span className="font-sans text-gold text-sm">Price varies</span>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}